import { type CSSProperties, useRef } from 'react';
import {
	type DraggableProvided,
	type DraggableStateSnapshot,
	type DraggingStyle,
} from 'react-beautiful-dnd';

import { getAvatars, getDropArea, getLaidOutRect, parseTranslate } from '../talentReviewDropDom';
import { useTalentReviewInsertion } from '../talentReviewInsertion';
import { useTalentReviewMemberOrder } from '../talentReviewMemberOrder';

const isDraggingStyle = (
	style: DraggableProvided['draggableProps']['style'],
): style is DraggingStyle => !!style && 'top' in style;

/**
 * Where a dropped person ends up in a wrapping flex container of avatars: at the
 * spot of the avatar it mounts before (which then shifts right), after the
 * avatar it mounts after (or at the start of the next row when it does not fit),
 * or, without either, at the very start. Measured from the DOM, as the container
 * may already have wrapped to a next row.
 * @param droppableId the droppable id
 * @param width width of the dragged avatar
 * @param neighbours the avatar the person mounts before or after; neither to go first
 * @param neighbours.before the avatar the person mounts in front of (its spot is taken over)
 * @param neighbours.after the avatar the person mounts behind
 * @returns viewport coordinates of the spot, or undefined when the area is not found
 */
const getDropSpot = (
	droppableId: string,
	width: number,
	neighbours: { before?: HTMLElement; after?: HTMLElement },
) => {
	const area = getDropArea(droppableId);
	if (!area) {
		return undefined;
	}

	if (neighbours.before) {
		const bounds = getLaidOutRect(neighbours.before);
		return { x: bounds.left, y: bounds.top };
	}

	const bounds = area.getBoundingClientRect();
	const computed = getComputedStyle(area);
	const inner = {
		left: bounds.left + parseFloat(computed.paddingLeft),
		top: bounds.top + parseFloat(computed.paddingTop),
		right: bounds.right - parseFloat(computed.paddingRight),
	};

	const after = neighbours.after && getLaidOutRect(neighbours.after);
	if (!after) {
		return { x: inner.left, y: inner.top };
	}

	const spot = { x: after.right + (parseFloat(computed.columnGap) || 0), y: after.top };
	if (spot.x + width > inner.right + 0.5) {
		return { x: inner.left, y: after.bottom + (parseFloat(computed.rowGap) || 0) };
	}

	return spot;
};

/**
 * The pool avatar a person dropped into the pool mounts before: the first one
 * that comes later in the member order. Undefined when the person goes last, or
 * when the order is unknown.
 * @param droppableId the pool's droppable id
 * @param draggableId the dropped person
 * @param memberOrder member ids in pool order
 * @returns the avatar element, if any
 */
const getPoolSuccessor = (
	droppableId: string,
	draggableId: string,
	memberOrder: ReadonlyArray<string>,
) => {
	const area = getDropArea(droppableId);
	const index = memberOrder.indexOf(draggableId);
	if (!area || index === -1) {
		return undefined;
	}

	return getAvatars(area).find(
		(element) => memberOrder.indexOf(element.dataset.rbdDraggableId ?? '') > index,
	);
};

/**
 * The avatars a person dropped into a cell at `index` lands between: the one it
 * mounts after, or none when it goes first. A cell shows the gap in front of the
 * avatar at `index` (a margin), so that avatar's own spot is not where the person
 * lands; the spot follows from the avatar before it instead.
 * @param droppableId the cell's droppable id
 * @param draggableId the dropped person, skipped when it is rendered in this cell
 * @param index the tracked insertion index among the other avatars
 * @returns the neighbour to mount after, if any
 */
const getCellNeighbours = (droppableId: string, draggableId: string, index: number) => {
	const area = getDropArea(droppableId);
	if (!area) {
		return {};
	}

	const others = getAvatars(area).filter(
		(element) => element.dataset.rbdDraggableId !== draggableId,
	);
	return { after: others[Math.min(index, others.length) - 1] };
};

/**
 * Where a focused (lifted) person dropped into a cell ends up: centered over the cell.
 * @param droppableId the cell's droppable id
 * @param width width of the dragged avatar
 * @param height height of the dragged avatar
 * @returns viewport coordinates of the spot, or undefined when the cell is not found
 */
const getCellCenter = (droppableId: string, width: number, height: number) => {
	const container = document.querySelector<HTMLElement>(`[data-rbd-droppable-id="${droppableId}"]`);
	if (!container) {
		return undefined;
	}

	const bounds = container.getBoundingClientRect();
	return {
		x: bounds.left + (bounds.width - width) / 2,
		y: bounds.top + (bounds.height - height) / 2,
	};
};

/**
 * Drop animation for a person released over a grid cell or the unplaced pool.
 *
 * react-beautiful-dnd aims its drop animation at a placeholder along a single axis.
 * Cells hide that placeholder and wrap their avatars, so dnd's target is off as soon
 * as a cell has more than one row; the pool has a title above its avatars, so dnd
 * aims at that title. Aim the animation at the spot where the person will actually
 * be rendered on drag end instead: in a cell the tracked insertion point (see
 * `talentReviewInsertion`), in the pool the member order. The avatar that mounts
 * there takes over without moving. The focused person is rendered lifted and
 * centered in a cell, so aim there instead.
 * @returns a function that returns the style overrides for a draggable, if any
 */
export default function useTalentReviewDropStyle() {
	const releaseTransform = useRef<string | null | undefined>();
	const memberOrder = useTalentReviewMemberOrder();
	const insertion = useTalentReviewInsertion();

	return (
		provided: DraggableProvided,
		snapshot: DraggableStateSnapshot,
		/** The droppable the person is dragged from. */
		homeDroppableId?: string,
		/** Whether the person is the focused one, and so lands centered in the cell. */
		focused?: boolean,
	): CSSProperties | undefined => {
		const style = provided.draggableProps.style;
		if (!snapshot.isDragging || !isDraggingStyle(style)) {
			return undefined;
		}

		if (!snapshot.isDropAnimating) {
			releaseTransform.current = style.transform;
			return undefined;
		}

		const target = snapshot.draggingOver;
		const intoCell = !!target?.startsWith('cell-');
		const intoPool = target === 'team';
		if (!target || (!intoCell && !intoPool)) {
			return undefined;
		}

		const draggableId = provided.draggableProps['data-rbd-draggable-id'];
		const index = insertion?.target?.droppableId === target ? insertion.target.index : undefined;
		// Dropped back where they were: glide home. In a cell that is only the case
		// when the person also keeps their place in the order.
		const home = target === homeDroppableId && (!intoCell || index === undefined);

		let offset = { x: 0, y: 0 };
		if (!home) {
			let spot: { x: number; y: number } | undefined;
			if (intoCell && focused) {
				spot = getCellCenter(target, style.width, style.height);
			} else if (intoCell) {
				spot = getDropSpot(
					target,
					style.width,
					index === undefined
						? { after: getCellNeighbours(target, draggableId, Number.MAX_SAFE_INTEGER).after }
						: getCellNeighbours(target, draggableId, index),
				);
			} else {
				spot = getDropSpot(target, style.width, {
					before: getPoolSuccessor(target, draggableId, memberOrder),
					after: undefined,
				});
			}
			if (!spot) {
				return undefined;
			}
			offset = { x: spot.x - style.left, y: spot.y - style.top };
		}

		// dnd finishes the drop on transitionend; without a change in transform none
		// fires, so leave dnd's own target in that case.
		const release = parseTranslate(releaseTransform.current);
		if (Math.abs(release.x - offset.x) < 0.01 && Math.abs(release.y - offset.y) < 0.01) {
			return undefined;
		}

		return { transform: `translate(${offset.x}px, ${offset.y}px)` };
	};
}
